import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Trash2,
  UserCheck,
  Mail,
  Loader,
  ChevronLeft,
  ChevronRight,
  Edit,
  Filter,
  X,
} from 'lucide-react';
import CustomSelect from '../components/Common/CustomSelect';
import {
  getAllUsers,
  updateUserStatus,
  deleteUser,
  UserManagementUser,
} from '../services/userManagementService';
import { toast } from 'react-toastify';
import Modal from '../components/Common/Modal';
import { MESSAGES } from '../utils/messages';
import { ROLES } from '../constants/roles';
import { paginationPageWindow } from '../utils/pagination';

const ITEMS_PER_PAGE = 10;

type FilterState = {
  status: string;
  role: string;
  createdFrom: string;
  createdTo: string;
};

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<UserManagementUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserManagementUser | null>(null);
  const [userToToggle, setUserToToggle] = useState<UserManagementUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    status: '',
    role: '',
    createdFrom: '',
    createdTo: '',
  });

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const usersList = await getAllUsers();
      setUsers(usersList);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return users.filter((user) => {
      const name = user.name?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      const role = user.role?.toLowerCase() || '';
      const matchesSearch = name.includes(q) || email.includes(q) || role.includes(q);

      if (filters.status) {
        const isActive = filters.status === 'active';
        if (user.active !== isActive) return false;
      }

      if (filters.role && user.role !== filters.role) {
        return false;
      }

      if (filters.createdFrom && user.createdAt) {
        const createdDate = new Date(user.createdAt);
        const fromDate = new Date(filters.createdFrom);
        if (createdDate < fromDate) return false;
      }

      if (filters.createdTo && user.createdAt) {
        const createdDate = new Date(user.createdAt);
        const toDate = new Date(filters.createdTo);
        toDate.setHours(23, 59, 59, 999);
        if (createdDate > toDate) return false;
      }

      return matchesSearch;
    });
  }, [users, searchTerm, filters]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  const clearFilters = () => {
    setFilters({ status: '', role: '', createdFrom: '', createdTo: '' });
    setSearchTerm('');
  };

  const handleToggleStatus = async () => {
    if (!userToToggle) return;

    setIsSubmitting(true);
    try {
      await updateUserStatus(userToToggle.id, !userToToggle.active);
      toast.success(MESSAGES.success.userUpdated);
      setIsStatusModalOpen(false);
      setUserToToggle(null);
      fetchUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update user status';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id);
      toast.success(MESSAGES.success.userDeleted);
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete user';
      toast.error(errorMessage);
    }
  };

  const openCreateModal = () => {
    navigate('/users/new');
  };

  const openEditModal = (user: UserManagementUser) => {
    navigate(`/users/${user.id}/edit`);
  };

  const openStatusModal = (user: UserManagementUser) => {
    setUserToToggle(user);
    setIsStatusModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        </div>
        <button onClick={openCreateModal} className="btn-primary flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Add User</span>
        </button>
      </div>

      {/* Search and Filter Card */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${hasActiveFilters ? 'ring-2 ring-primary-500' : ''}`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full font-medium">
                {Object.values(filters).filter((v) => v !== '').length}
              </span>
            )}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <CustomSelect
                  value={filters.status}
                  onChange={(val) => setFilters((prev) => ({ ...prev, status: val }))}
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  placeholder="All Statuses"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <CustomSelect
                  value={filters.role}
                  onChange={(val) => setFilters((prev) => ({ ...prev, role: val }))}
                  options={[
                    { value: '', label: 'All Roles' },
                    { value: ROLES.SUPERADMIN, label: 'Super Admin' },
                  ]}
                  placeholder="All Roles"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created From</label>
                <input
                  type="date"
                  value={filters.createdFrom}
                  onChange={(e) => setFilters((prev) => ({ ...prev, createdFrom: e.target.value }))}
                  className="input-field w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created To</label>
                <input
                  type="date"
                  value={filters.createdTo}
                  onChange={(e) => setFilters((prev) => ({ ...prev, createdTo: e.target.value }))}
                  className="input-field w-full text-sm"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex justify-end space-x-3 mt-4">
                <button onClick={clearFilters} className="btn-secondary text-sm flex items-center">
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="card text-center py-12">
          <Loader className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading users...</h3>
          <p className="text-gray-600">Please wait while we fetch the user list.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && users.length === 0 && (
        <div className="card text-center py-12">
          <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users yet</h3>
          <p className="text-gray-600 mb-4">Get started by creating your first user account.</p>
          <button onClick={openCreateModal} className="btn-primary inline-flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Add User</span>
          </button>
        </div>
      )}

      {/* No Search Results */}
      {!isLoading && users.length > 0 && filteredUsers.length === 0 && (
        <div className="card text-center py-12">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
          <p className="text-gray-600">
            {hasActiveFilters ? 'Try adjusting your filters or search criteria.' : 'Try adjusting your search criteria.'}
          </p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="mt-4 text-sm text-primary-600 hover:text-primary-700">
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* User Table */}
      {!isLoading && filteredUsers.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Email
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Role
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Created
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <UserCheck className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                          <div className="text-xs text-gray-500 sm:hidden truncate">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => openStatusModal(user)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          user.active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                        title={`Click to ${user.active ? 'deactivate' : 'activate'} user`}
                      >
                        {user.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-4">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-primary-600 hover:text-primary-900 transition-colors duration-200"
                          title="Edit user"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setUserToDelete(user);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-sm text-gray-500">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} of{' '}
                {filteredUsers.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="flex items-center gap-1">
                  {paginationPageWindow(currentPage, totalPages).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`h-8 w-8 rounded-lg text-sm font-medium border flex items-center justify-center transition-colors ${
                        currentPage === page
                          ? 'bg-primary-600 border-primary-600 text-white cursor-default'
                          : 'bg-white border-gray-200 text-gray-500 hover:bg-primary-50 hover:text-primary-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status Update Confirmation Modal */}
      <Modal
        title="Update User Status"
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false);
          setUserToToggle(null);
        }}
        widthClassName="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to{' '}
            <span className="font-medium">{userToToggle?.active ? 'deactivate' : 'activate'}</span>{' '}
            user <span className="font-medium">{userToToggle?.name}</span>?
          </p>
          <p className="text-sm text-gray-500">
            {userToToggle?.active
              ? 'Deactivating this user will prevent them from logging in.'
              : 'Activating this user will allow them to log in.'}
          </p>
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => {
                setIsStatusModalOpen(false);
                setUserToToggle(null);
              }}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleToggleStatus}
              className={`btn-primary ${userToToggle?.active ? 'bg-danger-500 hover:bg-danger-600' : 'bg-green-600 hover:bg-green-700'}`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Updating...'
                : userToToggle?.active
                ? 'Deactivate'
                : 'Activate'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete User"
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        widthClassName="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete{' '}
            <span className="font-medium">{userToDelete?.name}</span>?
          </p>
          <p className="text-sm text-gray-500">
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => {
                setIsDeleteModalOpen(false);
                setUserToDelete(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
